<script setup lang="ts">
import { computed } from 'vue';
import { UiInputField, UiStandardButton, UiText } from '@shopexperts/shared';
import type { PageSchema } from '@shopexperts/shared';

const props = defineProps<{
  schemas: PageSchema[];
  selectedSchemaId?: number | null;
  isLoading: boolean;
}>();

const search = defineModel<string>('search', { required: true });

const emit = defineEmits<{
  selectSchema: [schema: PageSchema];
  createSchema: [];
}>();

const filteredSchemas = computed(() => {
  const query = search.value.trim().toLowerCase();
  if (!query) return props.schemas;
  return props.schemas.filter((schema) => (
    schema.label.toLowerCase().includes(query)
    || schema.name.toLowerCase().includes(query)
  ));
});
</script>

<template>
  <aside class="flex min-h-0 w-72 shrink-0 flex-col overflow-hidden border-r border-se-gray-300 bg-white">
    <div class="border-b border-se-gray-300 px-4 py-3">
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0">
          <UiText as="h2" variant="h6" text="Schemas" />
          <UiText as="p" variant="caption-normal" class="mt-1 text-se-gray-700" text="Select a schema to edit." />
        </div>
        <UiStandardButton
          label="New"
          icon="plus"
          variant="outline"
          class="shrink-0"
          @click="emit('createSchema')"
        />
      </div>
    </div>

    <div class="border-b border-se-gray-300 p-4">
      <UiInputField v-model="search" placeholder="Search schemas..." />
    </div>

    <div class="min-h-0 flex-1 overflow-y-auto p-3">
      <UiText v-if="props.isLoading" as="p" variant="p-small" text="Loading schemas..." class="p-3 text-se-gray-700" />
      <div v-else class="flex flex-col gap-1">
        <button
          v-for="schema in filteredSchemas"
          :key="schema.id"
          type="button"
          class="rounded-md px-3 py-2.5 text-left hover:bg-se-gray-100"
          :class="{ 'bg-se-gray-100 ring-1 ring-inset ring-se-gray-300': props.selectedSchemaId === schema.id }"
          @click="emit('selectSchema', schema)"
        >
          <UiText as="span" variant="p-small" class="block truncate text-se-dark" :text="schema.label" />
          <UiText as="span" variant="caption-normal" class="block truncate text-se-gray-700" :text="schema.name" />
        </button>
        <div v-if="!filteredSchemas.length" class="p-3">
          <UiText as="p" variant="p-small" class="text-se-gray-700" text="No schemas found." />
        </div>
      </div>
    </div>
  </aside>
</template>
